import React from 'react';
import { HistoryEntry } from '../../types/calculator';
import { Trash2 } from 'lucide-react';

interface HistoryTableProps {
  history: HistoryEntry[];
  onDelete: (id: string) => void;
  onClearAll: () => void;
}

export const HistoryTable: React.FC<HistoryTableProps> = ({
  history,
  onDelete,
  onClearAll,
}) => {
  if (history.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 mt-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-gray-800">Calculation History</h2>
        <button
          onClick={onClearAll}
          className="px-4 py-2 text-sm text-red-600 hover:text-red-700 focus:outline-none"
        >
          Clear All
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Vial Amount
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Dosage
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Reconstitution
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Units/Dose
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Concentration
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Total Doses
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {history.map((entry) => (
              <tr key={entry.id} className="hover:bg-gray-50">
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(entry.timestamp).toLocaleString()}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {entry.inputs.peptideName}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                  {entry.inputs.vialAmount} {entry.inputs.vialUnits}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                  {entry.inputs.dosage} {entry.inputs.dosageUnits}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                  {entry.metrics.reconstitutionAmount} {entry.metrics.reconstitutionUnits}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                  {entry.metrics.unitsPerDose}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                  {entry.metrics.finalConcentration} mg/ml
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                  {entry.metrics.totalDoses}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                  <button
                    onClick={() => onDelete(entry.id)}
                    className="text-red-600 hover:text-red-900 transition-colors"
                    title="Delete entry"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};